import classNames from "classnames"
import { useCallback, useEffect, useRef, useState, type ChangeEvent, type ReactNode } from "react"
import { Col, Row } from "react-bootstrap"
import Form from "react-bootstrap/Form"
import S from "./ImageField.module.css"
import saveAs from "file-saver"
import { Button, Icon } from "@inmagik/magik-ui"

export type FileFieldProps = {
  layout?: "vertical" | "horizontal"
  labelColSize?: number
  inputColSize?: number
  label?: string
  helperText?: string
  placeholder?: string
  value?: string | File | null
  onChange?: (value: File | null) => void
  readonly?: boolean
  disabled?: boolean
  isValid?: boolean
  successMessage?: string
  errorMessage?: string
  size?: "sm" | "md" | "lg"
  leadingIcon?: string | ReactNode
  accept?: string
  required?: boolean
  className?: string
  style?: React.CSSProperties
}

export function FileField({
  layout = "vertical",
  label,
  helperText,
  placeholder,
  labelColSize,
  inputColSize,
  value,
  onChange,
  readonly,
  disabled,
  isValid,
  successMessage,
  errorMessage,
  size,
  leadingIcon = "perm_media",
  required = false,
  className,
  style,
}: FileFieldProps) {
  const [url, setUrl] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (value instanceof File) {
      const url = URL.createObjectURL(value)
      setUrl(url)
      return () => URL.revokeObjectURL(url)
    } else {
      setUrl(value ?? null)
    }
  }, [value])

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.files?.[0] ?? null)
      e.target.value = ""
    },
    [onChange],
  )

  const requiredMarker = required ? <span className="text-danger">*</span> : undefined
  const labelCmp = label ? (
    <Form.Label className="mb-0">
      {label}
      {requiredMarker}
    </Form.Label>
  ) : undefined
  const helperTextCmp = helperText ? <Form.Text className="text-muted mt-0">{helperText}</Form.Text> : undefined
  const controlCmp = (
    <div
      className={classNames("position-relative", { "is-invalid": isValid === false, "is-valid": isValid === true })}
      onClick={() => {
        if (!disabled && !readonly) {
          inputRef.current?.click()
        }
      }}
    >
      <div className="px-2 position-absolute top-0 bottom-0 d-flex flex-column justify-content-center">
        {typeof leadingIcon === "string" ? <Icon icon={leadingIcon} className={S.icon} /> : leadingIcon}
      </div>
      <Form.Control
        size={size === "md" ? undefined : size}
        value={value instanceof File ? value.name : ""}
        placeholder={placeholder}
        readOnly={readonly}
        plaintext={readonly}
        disabled={disabled}
        isValid={isValid === true}
        isInvalid={isValid === false}
        className={classNames({ "p-0": readonly, "ps-6": !!leadingIcon })}
        style={{ pointerEvents: "none" }}
      ></Form.Control>
      <input
        type="file"
        ref={inputRef}
        style={{ display: "none" }}
        onChange={(e) => handleFileChange(e)}
        
      />
    </div>
  )
  let semanticTextCmp = undefined
  if (errorMessage) {
    semanticTextCmp = <Form.Control.Feedback type="invalid">{errorMessage}</Form.Control.Feedback>
  } else if (successMessage) {
    semanticTextCmp = <Form.Control.Feedback type="valid">{successMessage}</Form.Control.Feedback>
  }
  const extra = (
    <div className={S.preview}>
      <div className={S.previewActions}>
        {typeof value === "string" && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => saveAs(value, value.split("/").pop() ?? "file")}
            title="Download"
            trailingIcon="download"
          />
        )}
        <Button variant="danger" size="sm" onClick={() => onChange?.(null)} title="Rimuovi" trailingIcon="delete" />
      </div>
    </div>
  )

  if (layout === "horizontal") {
    return (
      <Form.Group as={Row} className={className} style={style}>
        <Col xs={labelColSize ?? 4} className="d-flex flex-column">
          {labelCmp}
          {helperTextCmp}
        </Col>
        <Col xs={inputColSize ?? 8} className="d-flex flex-column align-items-stretch">
          {!readonly ? controlCmp : null}
          {semanticTextCmp}
          {url ? extra : null}
        </Col>
      </Form.Group>
    )
  } else {
    return (
      <Form.Group as={Row} className={className} style={style}>
        <Col xs={inputColSize ?? 12} className="d-flex flex-column">
          {labelCmp}
          {helperTextCmp}
          {!readonly ? controlCmp : null}
          {semanticTextCmp}
          {url ? extra : null}
        </Col>
      </Form.Group>
    )
  }
}
